import React, { useEffect, useState } from "react";
import { Box, Button, Dialog, TextField, Typography } from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import dayjs from "dayjs";
import { isTpPolicyNumber, existingTpModeOfPolicy, routingOf } from "../utils/companyRules";
import { defaultTpDates, indiaToday, tpYearsLabel } from "../utils/tpDates";
import { SaveTpPolicyDetails } from "../../../../Service/_index";

const NAVY = "#1E3A5F";
const FONT = "Inter, Poppins, sans-serif";
const btn = { textTransform: "none", borderRadius: "8px", fontFamily: FONT, fontWeight: 600, fontSize: "13px", boxShadow: "none" };

// index.scss makes every outlined input border transparent: restated here so each box is visibly a box
const boxedField = {
  "& .MuiOutlinedInput-root": { backgroundColor: "#fff", borderRadius: "8px" },
  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: "#D0D5DD !important" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#98A2B3 !important" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1E3A5F !important", borderWidth: "2px !important" },
  "& .MuiInputBase-input": { fontFamily: FONT, fontSize: "13px" },
};

/** "2026-10-02" (the date box's value) from a saved date; "" when there is none. */
export const toDayInput = (value) => {
  if (!value) return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("YYYY-MM-DD") : "";
};

/**
 * The TP details as they start for a policy: what it already holds. A date it does not hold is worked out (the owner,
 * 2026-10-23): start = today, end = the policy's year selection after it (utils/tpDates.js) — shown here so it can be
 * changed before Save; the server works out a date left empty the same way.
 */
export const tpDetailsOf = (policy, now = Date.now()) => {
  const saved = {
    policyNumber: (policy && policy.existingTpPolicyNumber) || "",
    startDate: toDayInput(policy && policy.existingTpStartDate),
    endDate: toDayInput(policy && policy.existingTpEndDate),
  };
  const dates = defaultTpDates({ startDate: saved.startDate, endDate: saved.endDate, years: policy && policy.paCoverYears, now });
  return { ...saved, startDate: dates.startDate, endDate: dates.endDate };
};

/**
 * Why the TP details cannot be saved yet - null when they can. The server checks the same again. A date left empty is
 * worked out (today / the year selection), so only the dates that ARE entered are checked.
 */
export const tpDetailsProblem = ({ policyNumber, startDate, endDate }, now = Date.now()) => {
  if (!String(policyNumber || "").trim()) return "Enter the TP policy number";
  if (!isTpPolicyNumber(policyNumber)) return "The TP policy number looks wrong. Use letters, digits and / - only";
  const start = startDate || (endDate ? indiaToday(now) : "");
  if (start && endDate && String(endDate) <= String(start)) return "The TP end date must be after the TP start date";
  return null;
};

/**
 * The three boxes: TP Policy Number, TP Start Date, TP End Date (used here and in Edit -> Status). `years` = the policy's
 * "No of Year" (none = 1): the line under the boxes says what an empty date becomes.
 */
export const TpDetailsFields = ({ value, onChange, disabled, insurer, years }) => (
  <Box data-testid="tp-details-fields" sx={{ mt: 1.25 }}>
    {insurer ? (
      <Typography sx={{ fontSize: "12px", color: "#475467", fontFamily: FONT, mb: 1 }}>
        {insurer} TP policy - Reliance OD needs these and does not start without them
      </Typography>
    ) : null}
    <Box sx={{ display: "grid", gap: 1.25, gridTemplateColumns: { xs: "1fr", sm: "1.4fr 1fr 1fr" } }}>
      <TextField
        size="small"
        disabled={disabled}
        label="TP Policy Number"
        value={value.policyNumber}
        onChange={(e) => onChange({ ...value, policyNumber: e.target.value.toUpperCase() })}
        inputProps={{ "data-testid": "tp-policy-number" }}
        InputLabelProps={{ shrink: true }}
        sx={boxedField}
      />
      <TextField
        size="small"
        type="date"
        disabled={disabled}
        label="TP Start Date"
        value={value.startDate}
        onChange={(e) => onChange({ ...value, startDate: e.target.value })}
        inputProps={{ "data-testid": "tp-start-date" }}
        InputLabelProps={{ shrink: true }}
        sx={boxedField}
      />
      <TextField
        size="small"
        type="date"
        disabled={disabled}
        label="TP End Date"
        value={value.endDate}
        onChange={(e) => onChange({ ...value, endDate: e.target.value })}
        inputProps={{ "data-testid": "tp-end-date", min: value.startDate || undefined }}
        InputLabelProps={{ shrink: true }}
        sx={boxedField}
      />
    </Box>
    <Typography data-testid="tp-dates-hint" sx={{ fontSize: "12px", color: "#667085", fontFamily: FONT, mt: 0.75 }}>
      A date left empty is filled in automatically - the start is today, the end follows the {tpYearsLabel(years)} selected on the policy.
    </Typography>
  </Box>
);

/**
 * Edit -> TP policy details (2026-10-22): a policy sold by two insurers (Reliance OD + KSHEMA / National TP). The TP
 * policy is bought first; its number and dates are entered here by hand. Reliance OD does not start without them, and
 * one that was waiting is queued again by the server.
 */
const TpPolicyDialog = ({ policy, onClose, onChanged }) => {
  const open = !!policy;
  const [value, setValue] = useState({ policyNumber: "", startDate: "", endDate: "" });
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState("");
  // the dates start filled in (today / the year selection), so "something is there" no longer says the operator began typing
  const [edited, setEdited] = useState(false);

  useEffect(() => {
    if (!policy) return;
    setValue(tpDetailsOf(policy));
    setServerError("");
    setEdited(false);
  }, [policy]);

  if (!open) return null;
  const insurer = routingOf(policy).TPCompany || policy.existingTpInsurer || "TP";
  const problem = tpDetailsProblem(value);
  const touched = edited || value.policyNumber;
  const name = policy.fullName || policy.firstName || policy.policyId || "This policy";
  const twoInsurers = existingTpModeOfPolicy(policy) === "optional";

  const save = async () => {
    if (problem || busy) return;
    setBusy(true);
    setServerError("");
    try {
      const res = await SaveTpPolicyDetails({ policyId: policy._id || policy.id, ...value });
      if (res && !res.error) {
        if (onChanged) onChanged(res.data);
        onClose();
      } else {
        setServerError((res && res.message) || "Could not save the TP policy details");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "14px", m: { xs: 1.5, sm: 4 }, width: { xs: "calc(100% - 24px)", sm: undefined } } }}>
      <Box sx={{ p: { xs: 2, sm: 2.5 } }} data-testid="tp-policy-dialog">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
          <Box sx={{ width: 38, height: 38, borderRadius: "11px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: NAVY, backgroundColor: "#EEF3F9", border: "1px solid #C9D7E8" }}>
            <ShieldOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "15px", color: NAVY, fontFamily: FONT, lineHeight: 1.3 }}>TP policy details</Typography>
            <Typography sx={{ fontSize: "12.5px", color: "#475467", fontFamily: FONT }}>
              {name} · {insurer} TP
            </Typography>
          </Box>
        </Box>

        {twoInsurers ? (
          <TpDetailsFields value={value} onChange={(v) => { setServerError(""); setEdited(true); setValue(v); }} disabled={busy} insurer={insurer} years={policy.paCoverYears} />
        ) : (
          <Typography sx={{ fontSize: "13px", color: "#B42318", fontFamily: FONT }}>
            TP policy details are entered here only for a policy sold by two insurers.
          </Typography>
        )}

        {touched && problem ? (
          <Typography data-testid="tp-policy-problem" sx={{ mt: 1, fontSize: "12px", color: "#B42318", fontFamily: FONT }}>
            {problem}
          </Typography>
        ) : null}

        {serverError ? (
          <Box sx={{ mt: 1.5, px: 1.5, py: 1, borderRadius: "10px", backgroundColor: "#FEF3F2", border: "1px solid #FECDCA" }}>
            <Typography data-testid="tp-policy-server-error" sx={{ fontSize: "12.5px", color: "#B42318", fontFamily: FONT }}>
              {serverError}
            </Typography>
          </Box>
        ) : null}

        <Typography sx={{ mt: 1.5, fontSize: "12px", color: "#475467", fontFamily: FONT }}>
          Saving puts Reliance OD back in the queue if it was waiting for these.
        </Typography>

        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 2, flexWrap: "wrap" }}>
          <Button onClick={onClose} disabled={busy} sx={{ ...btn, color: "#475467" }}>
            Cancel
          </Button>
          <Button variant="contained" disabled={!!problem || busy || !twoInsurers} onClick={save} data-testid="tp-policy-save" sx={{ ...btn, backgroundColor: NAVY }}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default TpPolicyDialog;
